/**
 * Finance data lives as raw JSON files next to the synced database rather
 * than inside it (it's append-heavy and year-partitioned, so it doesn't
 * belong in the whole-database blob). That means anything which moves a
 * user's data — provider switching, JSON export/import — has to move these
 * files explicitly, or the user silently loses every transaction.
 *
 * finance-settings.json is the manifest: it lists which years exist.
 */
export const FINANCE_SETTINGS_FILE = 'finance-settings.json';
export const FINANCE_INVESTMENTS_FILE = 'finance-investments.json';
export const FINANCE_INCOME_FILE = 'finance-income.json';
/** Fixed files that always exist once finance has been used at all. */
const FIXED_FINANCE_FILES = [
    FINANCE_SETTINGS_FILE,
    FINANCE_INVESTMENTS_FILE,
    FINANCE_INCOME_FILE,
];
/**
 * Every finance file name present for this user, discovered from the
 * settings manifest (which years exist) plus the fixed files.
 */
export async function listFinanceFiles(provider) {
    const names = [...FIXED_FINANCE_FILES];
    const settingsRaw = await provider.readRawFile(FINANCE_SETTINGS_FILE);
    if (settingsRaw) {
        try {
            const settings = JSON.parse(settingsRaw);
            for (const year of settings.years ?? []) {
                names.push(`finance-${year}.json`);
            }
        }
        catch {
            // Unreadable manifest — still carry the fixed files across
        }
    }
    return names;
}
/**
 * Read every finance file into a { filename: content } map. Files that don't
 * exist are skipped, so a user who never opened Finance yields {}.
 */
export async function collectFinanceFiles(provider) {
    const names = await listFinanceFiles(provider);
    const entries = await Promise.all(names.map(async (name) => [name, await provider.readRawFile(name)]));
    const out = {};
    for (const [name, content] of entries) {
        if (content !== null)
            out[name] = content;
    }
    return out;
}
/**
 * Write a finance file map into a provider. Used when switching providers
 * and when importing a JSON backup.
 */
export async function restoreFinanceFiles(provider, files, message = 'finance: restore') {
    const entries = Object.entries(files);
    await Promise.all(entries.map(([name, content]) => provider.writeRawFile(name, content, message)));
    return entries.length;
}
//# sourceMappingURL=finance-files.js.map